import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Fade,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
  Divider,
  IconButton,
  alpha,
} from "@mui/material";
import TuneIcon from "@mui/icons-material/Tune";
import HistoryOutlined from "@mui/icons-material/HistoryOutlined";
import AddCircleOutline from "@mui/icons-material/AddCircleOutline";
import SelectAllIcon from "@mui/icons-material/SelectAll";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import ClearAllIcon from "@mui/icons-material/ClearAll";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import PlaylistAddCheckIcon from "@mui/icons-material/PlaylistAddCheck";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import {
  investmentTaxAdjustmentOptions_tagging,
  taxAdjustmentOptions_tagging,
} from "../../../utils/static/detailsPL";
import { formatAmount } from "../../../utils/hooks/dataHandler";
import usePreventPageUnload from "../../../utils/hooks/usePreventPageUnload";
import DialogLoading from "../../App/DialogLoading";
import BaseDialog from "../../utils/dialog/BaseDialog";
import GroupedSelect from "../../utils/GroupedSelect";
import {
  ROW_TYPE_OPTIONS,
  buildSchedulePayload,
  getInitialScheduleState,
  isRowTypeRequired,
  resolveScheduleType,
} from "./utils/scheduleEditorMapping";
import { fetchSchedulesByType } from "./services/scheduleEditorService";
import { useScheduleTagging } from "./hooks/useScheduleTagging";

const BASIC_ADJUSTMENTS = ["", "Added Back", "Deduct"];
const UNSET_ADJUSTMENT = "__unset__";
const ALL_ADJUSTMENT_FILTER = "__all__";
const NO_ADJUSTMENT_FILTER = "__none__";
const BATCH_NEW_SCHEDULE_ID = "__create_new_schedule__";
const SECTION_KEYS = ["Revenue", "Other Income", "Cost of Sales", "Expenses"];
const DIALOG_MODE_GUIDED = "guided";
const DIALOG_MODE_BATCH_TAG = "batchTag";

const panelSx = {
  minHeight: 0,
  border: "1px solid",
  borderColor: "divider",
  borderRadius: 2,
  bgcolor: "background.paper",
  overflow: "hidden",
};

const modeButtonSx = (selected) => (theme) => ({
  px: 1.5,
  py: 0.75,
  minWidth: "auto",
  borderRadius: 2,
  textTransform: "none",
  fontWeight: 600,
  color: selected ? "primary.dark" : "text.secondary",
  bgcolor: selected ? "background.paper" : "transparent",
  boxShadow: selected
    ? `0 1px 4px ${alpha(theme.palette.primary.main, 0.12)}`
    : "none",
  "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.07) },
});

export default function AdjustmentManagementDialog({
  open,
  onClose,
  companyType = "common",
  tcId,
  detailsId,
  rowsBySection,
  sectionKeys = SECTION_KEYS,
  source = "p_l",
  reference = "P&L",
  rowLabel = "P&L rows",
  onApply,
  handleError,
}) {
  const {
    getPlScheduleByType,
    getCaScheduleByType,
    tagSchedule,
    createTagSchedule,
    unTagSchedule,
  } = useScheduleTagging();
  const [selectedIds, setSelectedIds] = useState([]);
  const [dialogMode, setDialogMode] = useState(DIALOG_MODE_GUIDED);
  const [managementMode, setManagementMode] = useState("needsScheduleTag");
  const [adjustmentFilter, setAdjustmentFilter] = useState(
    ALL_ADJUSTMENT_FILTER
  );
  const [batchAdjustmentFilter, setBatchAdjustmentFilter] = useState(
    ALL_ADJUSTMENT_FILTER
  );
  const [batchAssignments, setBatchAssignments] = useState([]);
  const [batchSchedulesByType, setBatchSchedulesByType] = useState({});
  const [loadingBatchSchedules, setLoadingBatchSchedules] = useState(false);
  const [adjustmentType, setAdjustmentType] = useState(UNSET_ADJUSTMENT);
  const [rowType, setRowType] = useState("");
  const [scheduleMode, setScheduleMode] = useState("existing");
  const [scheduleId, setScheduleId] = useState("");
  const [scheduleTitle, setScheduleTitle] = useState("");
  const [schedules, setSchedules] = useState([]);
  const [loadingSchedules, setLoadingSchedules] = useState(false);
  const [autoSelecting, setAutoSelecting] = useState(false);
  const [applying, setApplying] = useState(false);
  const [localError, setLocalError] = useState("");
  const [autoSelectionNotice, setAutoSelectionNotice] = useState("");
  const [autoSelectionNoticeOpen, setAutoSelectionNoticeOpen] = useState(false);
  const lastApplySummaryRef = useRef(null);
  const targetAdjustmentTouchedRef = useRef(false);
  const autoSelectionNoticeTimersRef = useRef({ hide: null, clear: null });

  usePreventPageUnload(applying);

  const adjustmentOptions =
    companyType === "investment"
      ? investmentTaxAdjustmentOptions_tagging
      : taxAdjustmentOptions_tagging;

  const clearAutoSelectionNoticeTimers = useCallback(() => {
    const timers = autoSelectionNoticeTimersRef.current;
    if (timers.hide) {
      clearTimeout(timers.hide);
      timers.hide = null;
    }
    if (timers.clear) {
      clearTimeout(timers.clear);
      timers.clear = null;
    }
  }, []);

  const showAutoSelectionNotice = useCallback(
    (message) => {
      clearAutoSelectionNoticeTimers();
      setAutoSelectionNotice(message);
      setAutoSelectionNoticeOpen(true);
      autoSelectionNoticeTimersRef.current.hide = setTimeout(() => {
        setAutoSelectionNoticeOpen(false);
      }, 1700);
      autoSelectionNoticeTimersRef.current.clear = setTimeout(() => {
        setAutoSelectionNotice("");
      }, 2200);
    },
    [clearAutoSelectionNoticeTimers]
  );

  const hideAutoSelectionNotice = useCallback(() => {
    clearAutoSelectionNoticeTimers();
    setAutoSelectionNoticeOpen(false);
    autoSelectionNoticeTimersRef.current.clear = setTimeout(() => {
      setAutoSelectionNotice("");
    }, 250);
  }, [clearAutoSelectionNoticeTimers]);

  const setupAdjustmentOptions = useMemo(
    () => [
      {
        value: UNSET_ADJUSTMENT,
        label: "Choose an adjustment type",
        group: "Start here",
      },
      ...adjustmentOptions,
    ],
    [adjustmentOptions]
  );

  const allRows = useMemo(
    () => flattenRows(rowsBySection, sectionKeys),
    [rowsBySection, sectionKeys]
  );

  const rowsNeedingScheduleTag = useMemo(
    () => allRows.filter((item) => rowNeedsScheduleTag(item.row)),
    [allRows]
  );

  const batchAdjustmentFilterOptions = useMemo(() => {
    const uniqueAdjustments = Array.from(
      new Set(
        rowsNeedingScheduleTag
          .map((item) => item.row.taxAdjustment)
          .filter(Boolean)
      )
    );

    return [
      {
        value: ALL_ADJUSTMENT_FILTER,
        label: "All schedule types",
        group: "Filter",
      },
      ...uniqueAdjustments.map((value) => ({
        value,
        label: value,
        group: "Adjustment Type",
      })),
    ];
  }, [rowsNeedingScheduleTag]);

  const visibleBatchAssignments = useMemo(() => {
    if (batchAdjustmentFilter === ALL_ADJUSTMENT_FILTER) {
      return batchAssignments;
    }

    return batchAssignments.filter(
      (assignment) => assignment.adjustmentType === batchAdjustmentFilter
    );
  }, [batchAdjustmentFilter, batchAssignments]);

  const selectedBatchAssignments = useMemo(
    () => batchAssignments.filter((assignment) => assignment.selected),
    [batchAssignments]
  );
  const batchNewScheduleOwners = useMemo(
    () => getBatchNewScheduleOwners(batchAssignments),
    [batchAssignments]
  );

  const batchScheduleTypeKey = useMemo(() => {
    return Array.from(
      new Set(batchAssignments.map((assignment) => assignment.scheduleType))
    )
      .filter(Boolean)
      .sort()
      .join("|");
  }, [batchAssignments]);

  const taggedRows = useMemo(
    () => allRows.filter((item) => item.row.schedule),
    [allRows]
  );

  const rowsWithoutAdjustment = useMemo(
    () => allRows.filter((item) => rowHasNoAdjustment(item.row)),
    [allRows]
  );

  const modeRows = useMemo(() => {
    if (managementMode === "needsScheduleTag") return rowsNeedingScheduleTag;
    if (managementMode === "noAdjustment") return rowsWithoutAdjustment;
    return taggedRows;
  }, [
    managementMode,
    rowsNeedingScheduleTag,
    rowsWithoutAdjustment,
    taggedRows,
  ]);

  const adjustmentFilterOptions = useMemo(() => {
    return [
      {
        value: ALL_ADJUSTMENT_FILTER,
        label: "All adjustments",
        group: "Filter",
      },
      ...adjustmentOptions.map((option) => ({
        ...option,
        value: option.value || NO_ADJUSTMENT_FILTER,
      })),
    ];
  }, [adjustmentOptions]);

  const visibleRows = useMemo(() => {
    return getRowsByAdjustmentFilter(modeRows, adjustmentFilter);
  }, [adjustmentFilter, modeRows]);

  const selectedRows = useMemo(
    () => visibleRows.filter((item) => selectedIds.includes(item.id)),
    [selectedIds, visibleRows]
  );
  const firstSelectedRowDescription = selectedRows[0]?.row?.name?.trim() || "";
  const firstNeedsTagRow = rowsNeedingScheduleTag[0] || null;

  const hasAdjustmentChoice = adjustmentType !== UNSET_ADJUSTMENT;
  const scheduleType = hasAdjustmentChoice
    ? resolveScheduleType(adjustmentType, companyType)
    : "";
  const needsSchedule =
    hasAdjustmentChoice && !BASIC_ADJUSTMENTS.includes(adjustmentType);
  const needsRowType =
    hasAdjustmentChoice && isRowTypeRequired(adjustmentType, companyType);
  const rowTypeOptions = ROW_TYPE_OPTIONS[scheduleType] || [];
  const selectedScheduleForAction = schedules.find(
    (schedule) => String(schedule.sch_id) === String(scheduleId)
  );
  const selectedRowTypeLabel =
    rowTypeOptions.find((option) => option.value === rowType)?.label || rowType;
  const taggingActionSummary = getTaggingActionSummary({
    selectedCount: selectedRows.length,
    adjustmentType,
    hasAdjustmentChoice,
    needsSchedule,
    needsRowType,
    rowTypeLabel: selectedRowTypeLabel,
    scheduleMode,
    scheduleTitle,
    selectedSchedule: selectedScheduleForAction,
  });
  const isBatchTagMode = dialogMode === DIALOG_MODE_BATCH_TAG;
  const canApplyBatch =
    selectedBatchAssignments.length > 0 &&
    !applying &&
    !loadingBatchSchedules &&
    selectedBatchAssignments.every((assignment) =>
      isBatchAssignmentReady(assignment, batchSchedulesByType, companyType)
    ) &&
    Array.from(batchNewScheduleOwners.values()).every((assignment) =>
      Boolean(getBatchNewScheduleTitle(assignment))
    );
  const blockingLoading = applying || autoSelecting || loadingBatchSchedules;
  const blockingLoadingTitle = applying
    ? isBatchTagMode
      ? "Applying schedule tags..."
      : "Applying adjustments..."
    : isBatchTagMode
      ? "Loading schedules..."
      : "Preparing smart selection...";
  const blockingLoadingContent = applying
    ? `Please wait while the selected ${rowLabel} are tagged and saved.`
    : isBatchTagMode
      ? "Please wait while the available schedules are refreshed."
      : "Please wait while the matching adjustment setup is prepared.";

  useEffect(() => {
    if (!open) return;

    const hasPendingApplySummary = Boolean(lastApplySummaryRef.current);
    const defaultIds = allRows
      .filter((item) => rowNeedsScheduleTag(item.row))
      .map((item) => item.id);
    const fallbackIds = allRows
      .filter((item) => item.row.schedule)
      .map((item) => item.id);
    const noAdjustmentIds = allRows
      .filter((item) => rowHasNoAdjustment(item.row))
      .map((item) => item.id);

    setManagementMode(
      hasPendingApplySummary || defaultIds.length
        ? "needsScheduleTag"
        : fallbackIds.length
          ? "tagged"
          : "noAdjustment"
    );
    setAdjustmentFilter(ALL_ADJUSTMENT_FILTER);
    setSelectedIds(
      hasPendingApplySummary || defaultIds.length
        ? defaultIds
        : fallbackIds.length
          ? fallbackIds
          : noAdjustmentIds
    );
    setAdjustmentType(UNSET_ADJUSTMENT);
    setRowType("");
    setScheduleMode("existing");
    setScheduleId("");
    setScheduleTitle("");
    setSchedules([]);
    setDialogMode(DIALOG_MODE_GUIDED);
    setBatchAdjustmentFilter(ALL_ADJUSTMENT_FILTER);
    setBatchSchedulesByType({});
    setAutoSelecting(false);
    setLocalError("");
    targetAdjustmentTouchedRef.current = false;
    if (!hasPendingApplySummary) {
      hideAutoSelectionNotice();
    }
  }, [open, allRows, hideAutoSelectionNotice]);

  useEffect(() => {
    if (!open) {
      setBatchAssignments([]);
      setLoadingBatchSchedules(false);
      return;
    }

    setBatchAssignments((prev) =>
      mergeBatchAssignments(rowsNeedingScheduleTag, prev, companyType)
    );
  }, [open, rowsNeedingScheduleTag, companyType]);

  useEffect(() => {
    if (
      !open ||
      dialogMode !== DIALOG_MODE_BATCH_TAG ||
      !batchScheduleTypeKey
    ) {
      setLoadingBatchSchedules(false);
      return;
    }

    let active = true;
    const scheduleTypes = batchScheduleTypeKey.split("|").filter(Boolean);

    setLoadingBatchSchedules(true);
    setLocalError("");

    Promise.all(
      scheduleTypes.map(async (selectedScheduleType) => {
        const list = await fetchSchedulesByType({
          tcId,
          detailsId,
          selectedScheduleType,
          companyType,
          getPlScheduleByType,
          getCaScheduleByType,
        });

        return [selectedScheduleType, list];
      })
    )
      .then((entries) => {
        if (!active) return;

        const nextSchedulesByType = Object.fromEntries(entries);
        setBatchSchedulesByType(nextSchedulesByType);
        setBatchAssignments((prev) =>
          prev.map((assignment) => {
            const schedulesForType =
              nextSchedulesByType[assignment.scheduleType] || [];

            if (assignment.scheduleId === BATCH_NEW_SCHEDULE_ID) {
              return assignment;
            }

            if (
              assignment.scheduleId &&
              schedulesForType.some(
                (schedule) =>
                  String(schedule.sch_id) === String(assignment.scheduleId)
              )
            ) {
              return assignment;
            }

            return {
              ...assignment,
              scheduleId:
                schedulesForType.length === 1
                  ? String(schedulesForType[0].sch_id)
                  : "",
            };
          })
        );
      })
      .catch((error) => {
        if (!active) return;
        setBatchSchedulesByType({});
        setLocalError("Unable to load schedules for batch tagging.");
        handleError?.(error);
      })
      .finally(() => {
        if (active) {
          setLoadingBatchSchedules(false);
        }
      });

    return () => {
      active = false;
    };
  }, [
    open,
    dialogMode,
    batchScheduleTypeKey,
    tcId,
    detailsId,
    companyType,
    getPlScheduleByType,
    getCaScheduleByType,
    handleError,
  ]);

  useEffect(() => {
    if (!open || applying || managementMode !== "needsScheduleTag") {
      return;
    }

    if (!firstNeedsTagRow) {
      const lastApplySummary = lastApplySummaryRef.current;
      if (lastApplySummary) {
        showAutoSelectionNotice(
          `Applied ${lastApplySummary.count} row(s) for ${lastApplySummary.adjustment}. All rows that need schedule tags are now complete.`
        );
        lastApplySummaryRef.current = null;
      }
      return;
    }

    const lastApplySummary = lastApplySummaryRef.current;
    const remainingRowsForAppliedAdjustment = lastApplySummary
      ? rowsNeedingScheduleTag.filter(
          (item) => item.row.taxAdjustment === lastApplySummary.adjustment
        )
      : [];
    const nextAdjustment =
      remainingRowsForAppliedAdjustment[0]?.row.taxAdjustment ||
      firstNeedsTagRow.row.taxAdjustment;
    const sameAdjustmentRows = rowsNeedingScheduleTag.filter(
      (item) => item.row.taxAdjustment === nextAdjustment
    );

    setAutoSelecting(false);
    setAdjustmentFilter(nextAdjustment);
    setSelectedIds(sameAdjustmentRows.map((item) => item.id));

    if (lastApplySummary) {
      if (remainingRowsForAppliedAdjustment.length > 0) {
        showAutoSelectionNotice(
          `Applied ${lastApplySummary.count} row(s) for ${lastApplySummary.adjustment}. ${remainingRowsForAppliedAdjustment.length} more row(s) still need this schedule tag, so the same adjustment stayed selected.`
        );
      } else {
        showAutoSelectionNotice(
          `Applied ${lastApplySummary.count} row(s) for ${lastApplySummary.adjustment}. Smart selection moved to ${sameAdjustmentRows.length} row(s) for ${nextAdjustment}.`
        );
      }
      lastApplySummaryRef.current = null;
    } else {
      showAutoSelectionNotice(
        `Automatically selected ${sameAdjustmentRows.length} matching row(s) for ${nextAdjustment}.`
      );
    }
  }, [
    open,
    applying,
    managementMode,
    firstNeedsTagRow,
    rowsNeedingScheduleTag,
    showAutoSelectionNotice,
  ]);

  useEffect(() => {
    return () => {
      clearAutoSelectionNoticeTimers();
    };
  }, [clearAutoSelectionNoticeTimers]);

  useEffect(() => {
    if (
      !open ||
      targetAdjustmentTouchedRef.current ||
      adjustmentFilter === ALL_ADJUSTMENT_FILTER
    ) {
      return;
    }

    setAdjustmentType(
      adjustmentFilter === NO_ADJUSTMENT_FILTER ? "" : adjustmentFilter
    );
  }, [open, adjustmentFilter]);

  useEffect(() => {
    if (!open || !needsSchedule) {
      setSchedules([]);
      setScheduleId("");
      setLoadingSchedules(false);
      setAutoSelecting(false);
      return;
    }

    let active = true;
    setLoadingSchedules(true);
    setLocalError("");

    fetchSchedulesByType({
      tcId,
      detailsId,
      selectedScheduleType: adjustmentType,
      companyType,
      getPlScheduleByType,
      getCaScheduleByType,
    })
      .then((list) => {
        if (!active) return;
        setSchedules(list);
        if (list.length > 0) {
          setScheduleMode("existing");
          setScheduleId(String(list[0].sch_id));
        } else {
          setScheduleMode("new");
          setScheduleId("");
        }
      })
      .catch((error) => {
        if (!active) return;
        setSchedules([]);
        setScheduleId("");
        setLocalError("Unable to load schedules. Please try again.");
        handleError?.(error);
      })
      .finally(() => {
        if (active) {
          setLoadingSchedules(false);
          setAutoSelecting(false);
        }
      });

    return () => {
      active = false;
    };
  }, [
    open,
    needsSchedule,
    adjustmentType,
    companyType,
    tcId,
    detailsId,
    getPlScheduleByType,
    getCaScheduleByType,
    handleError,
  ]);

  useEffect(() => {
    if (!hasAdjustmentChoice) {
      setRowType("");
      setScheduleTitle("");
      return;
    }

    const initial = getInitialScheduleState(
      { taxAdjustment: adjustmentType },
      false
    );
    setRowType(initial.targetSchedule.rowType || "");
    setScheduleTitle(
      initial.targetSchedule.scheduleTitle || firstSelectedRowDescription
    );
  }, [adjustmentType, firstSelectedRowDescription, hasAdjustmentChoice]);

  const toggleRow = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleAdjustmentTypeChange = (event) => {
    targetAdjustmentTouchedRef.current = true;
    setAdjustmentType(event.target.value);
    setScheduleMode("existing");
    setScheduleId("");
    setSchedules([]);
    setAutoSelecting(false);
    setLocalError("");
    hideAutoSelectionNotice();
  };

  const changeManagementMode = (mode) => {
    const nextRows =
      mode === "needsScheduleTag"
        ? rowsNeedingScheduleTag
        : mode === "noAdjustment"
          ? rowsWithoutAdjustment
          : taggedRows;

    setManagementMode(mode);
    setAdjustmentFilter(ALL_ADJUSTMENT_FILTER);
    setSelectedIds(nextRows.map((item) => item.id));
  };

  const selectAll = () => setSelectedIds(visibleRows.map((item) => item.id));
  const selectSameAdjustmentRows = () => {
    const anchorRow = selectedRows[0] || visibleRows[0];
    if (!anchorRow) return;

    const nextFilter = anchorRow.row.taxAdjustment || NO_ADJUSTMENT_FILTER;
    const nextRows = getRowsByAdjustmentFilter(modeRows, nextFilter);

    setAdjustmentFilter(nextFilter);
    setAutoSelecting(false);
    setSelectedIds(nextRows.map((item) => item.id));
    showAutoSelectionNotice(
      `Selected ${nextRows.length} row(s) for ${anchorRow.row.taxAdjustment || "No Adjustment"}.`
    );
  };
  const clearSelection = () =>
    setSelectedIds((prev) =>
      prev.filter((id) => !visibleRows.some((item) => item.id === id))
    );

  const openBatchTagMode = () => {
    setDialogMode(DIALOG_MODE_BATCH_TAG);
    setBatchAdjustmentFilter(ALL_ADJUSTMENT_FILTER);
    hideAutoSelectionNotice();
  };

  const updateBatchAssignment = (id, patch) => {
    setBatchAssignments((prev) => {
      const nextAssignments = prev.map((assignment) =>
        assignment.id === id
          ? { ...assignment, ...patch, error: "" }
          : assignment
      );

      return normalizeBatchNewScheduleOwners(nextAssignments, prev);
    });
  };

  const toggleBatchAssignment = (id) => {
    setBatchAssignments((prev) => {
      const nextAssignments = prev.map((assignment) =>
        assignment.id === id
          ? { ...assignment, selected: !assignment.selected }
          : assignment
      );

      return normalizeBatchNewScheduleOwners(nextAssignments, prev);
    });
  };

  const selectAllVisibleBatchAssignments = () => {
    const visibleIds = new Set(
      visibleBatchAssignments.map((assignment) => assignment.id)
    );

    setBatchAssignments((prev) =>
      prev.map((assignment) =>
        visibleIds.has(assignment.id)
          ? { ...assignment, selected: true }
          : assignment
      )
    );
  };

  const clearVisibleBatchAssignments = () => {
    const visibleIds = new Set(
      visibleBatchAssignments.map((assignment) => assignment.id)
    );

    setBatchAssignments((prev) => {
      const nextAssignments = prev.map((assignment) =>
        visibleIds.has(assignment.id)
          ? { ...assignment, selected: false }
          : assignment
      );

      return normalizeBatchNewScheduleOwners(nextAssignments, prev);
    });
  };

  const canApply =
    selectedRows.length > 0 &&
    hasAdjustmentChoice &&
    !blockingLoading &&
    !loadingSchedules &&
    (!needsRowType || Boolean(rowType)) &&
    (!needsSchedule ||
      (scheduleMode === "existing"
        ? Boolean(scheduleId)
        : Boolean(scheduleTitle)));

  const handleApply = async () => {
    if (!canApply) {
      setLocalError("Complete the setup before applying adjustments.");
      return;
    }

    setApplying(true);
    setLocalError("");

    try {
      const nextRowsBySection = cloneRowsBySection(rowsBySection, sectionKeys);
      let newlyCreatedSchedule = null;

      for (const item of selectedRows) {
        const currentRow = nextRowsBySection[item.section][item.index];
        let updatedRow;

        if (BASIC_ADJUSTMENTS.includes(adjustmentType)) {
          if (currentRow.schedule) {
            await unTagSchedule(
              source,
              currentRow.row_id,
              currentRow.schedule.scheduleType === "FAAdditions"
                ? "ca_schedule"
                : "pl_schedule",
              false
            );
          }

          updatedRow = {
            ...currentRow,
            taxAdjustment: adjustmentType,
          };
          delete updatedRow.schedule;
        } else if (scheduleMode === "existing") {
          if (currentRow.schedule) {
            await unTagSchedule(
              source,
              currentRow.row_id,
              currentRow.schedule.scheduleType === "FAAdditions"
                ? "ca_schedule"
                : "pl_schedule",
              false
            );
            delete currentRow.schedule;
          }

          updatedRow = await applyExistingSchedule({
            item,
            row: currentRow,
            scheduleId,
            schedules,
            rowType,
            companyType,
            tcId,
            detailsId,
            adjustmentType,
            reference,
            tagSchedule,
          });
        } else {
          if (currentRow.schedule) {
            await unTagSchedule(
              source,
              currentRow.row_id,
              currentRow.schedule.scheduleType === "FAAdditions"
                ? "ca_schedule"
                : "pl_schedule",
              false
            );
            delete currentRow.schedule;
          }

          if (!newlyCreatedSchedule) {
            updatedRow = await applyNewSchedule({
              item,
              row: currentRow,
              scheduleTitle,
              rowType,
              companyType,
              tcId,
              detailsId,
              adjustmentType,
              reference,
              createTagSchedule,
            });
            if (!updatedRow?.schedule?.sch_id) {
              throw new Error(
                "The new schedule was created without a schedule ID."
              );
            }
            newlyCreatedSchedule = updatedRow.schedule;
          } else {
            updatedRow = await tagRowToSchedule({
              item,
              row: currentRow,
              schedule: newlyCreatedSchedule,
              rowType,
              companyType,
              tcId,
              detailsId,
              adjustmentType,
              reference,
              tagSchedule,
            });
          }
        }

        nextRowsBySection[item.section][item.index] = {
          ...updatedRow,
          taxAdjustment: adjustmentType,
        };
      }

      lastApplySummaryRef.current = {
        count: selectedRows.length,
        adjustment: adjustmentType,
      };
      await onApply(nextRowsBySection, selectedRows.length);
    } catch (error) {
      lastApplySummaryRef.current = null;
      setLocalError("Unable to apply all adjustments. No save was completed.");
      handleError?.(error);
    } finally {
      setApplying(false);
    }
  };

  const handleBatchApply = async () => {
    if (!canApplyBatch) {
      setLocalError("Complete every selected batch row before applying.");
      return;
    }

    setApplying(true);
    setLocalError("");

    try {
      const nextRowsBySection = cloneRowsBySection(rowsBySection, sectionKeys);
      let appliedCount = 0;
      const createdSchedulesByType = new Map();

      for (const assignment of selectedBatchAssignments) {
        const currentRow =
          nextRowsBySection[assignment.section]?.[assignment.index];

        if (!currentRow) {
          throw new Error(`Unable to locate ${assignment.row.name}.`);
        }

        let schedule = null;
        let updatedRow = null;

        if (assignment.scheduleId === BATCH_NEW_SCHEDULE_ID) {
          schedule = createdSchedulesByType.get(assignment.scheduleType);

          if (!schedule) {
            updatedRow = await applyNewSchedule({
              item: assignment,
              row: currentRow,
              scheduleTitle: getBatchNewScheduleTitle(assignment),
              rowType: assignment.rowType,
              companyType,
              tcId,
              detailsId,
              adjustmentType: assignment.scheduleType,
              reference,
              createTagSchedule,
            });

            if (!updatedRow?.schedule?.sch_id) {
              throw new Error(
                `Unable to create a schedule for ${assignment.row.name}.`
              );
            }

            schedule = updatedRow.schedule;
            createdSchedulesByType.set(assignment.scheduleType, schedule);
          }
        } else {
          const schedulesForType =
            batchSchedulesByType[assignment.scheduleType] || [];
          schedule = schedulesForType.find(
            (candidate) =>
              String(candidate.sch_id) === String(assignment.scheduleId)
          );
        }

        if (!schedule) {
          throw new Error(
            `Please select a schedule for ${assignment.row.name}.`
          );
        }

        if (!updatedRow) {
          updatedRow = await tagRowToSchedule({
            item: assignment,
            row: currentRow,
            schedule,
            rowType: assignment.rowType,
            companyType,
            tcId,
            detailsId,
            adjustmentType: assignment.scheduleType,
            reference,
            tagSchedule,
          });
        }

        nextRowsBySection[assignment.section][assignment.index] = {
          ...updatedRow,
          taxAdjustment: currentRow.taxAdjustment,
        };
        appliedCount += 1;
      }

      lastApplySummaryRef.current = null;
      await onApply(nextRowsBySection, appliedCount);
    } catch (error) {
      setLocalError("Unable to complete batch schedule tagging.");
      handleError?.(error);
    } finally {
      setApplying(false);
    }
  };

  const handleDialogClose = () => {
    if (!blockingLoading) onClose();
  };

  return (
    <BaseDialog
      open={open}
      onClose={handleDialogClose}
      closeDisabled={blockingLoading}
      title='Manage adjustments'
      subtitle={
        isBatchTagMode
          ? `Choose a schedule for each of your selected ${rowLabel}.`
          : `Select ${rowLabel} and apply an adjustment to them together.`
      }
      icon={<TuneIcon />}
      maxWidth='lg'
      contentSx={{
        px: { xs: 2, sm: 3 },
        py: 2.5,
        "& .MuiOutlinedInput-root": {
          borderRadius: 2,
          bgcolor: "background.paper",
        },
      }}
      actionsSx={{
        px: { xs: 2, sm: 3 },
        py: 2,
        gap: 1,
        flexWrap: "wrap",
        "& > :not(:first-of-type)": { ml: 0 },
        "& .MuiButton-contained": { boxShadow: "none", py: 1, px: 2 },
      }}
      actions={
        isBatchTagMode ? (
          <>
            <Button
              variant='outlined'
              onClick={() => setDialogMode(DIALOG_MODE_GUIDED)}
              disabled={blockingLoading}
              startIcon={<ArrowBackIcon />}
              sx={{ borderRadius: 2, textTransform: "none", mr: "auto" }}
            >
              Back to adjustments
            </Button>
            <Button
              variant='text'
              color='inherit'
              onClick={handleDialogClose}
              disabled={blockingLoading}
              sx={{ borderRadius: 2, textTransform: "none" }}
            >
              Cancel
            </Button>
            <Button
              variant='contained'
              onClick={handleBatchApply}
              disabled={!canApplyBatch || blockingLoading}
              startIcon={
                blockingLoading ? (
                  <CircularProgress size={18} color='inherit' />
                ) : (
                  <PlaylistAddCheckIcon />
                )
              }
              sx={{ borderRadius: 2, textTransform: "none" }}
            >
              Apply tags
            </Button>
          </>
        ) : (
          <>
            <Stack
              direction='row'
              spacing={1}
              alignItems='center'
              sx={{ mr: "auto" }}
            >
              <Typography variant='body2' color='text.secondary'>
                {selectedRows.length} selected
              </Typography>
            </Stack>
            <Button
              variant='text'
              color='inherit'
              onClick={handleDialogClose}
              disabled={blockingLoading}
              sx={{ borderRadius: 2, textTransform: "none" }}
            >
              Cancel
            </Button>
            <Button
              variant='contained'
              onClick={handleApply}
              disabled={!canApply || blockingLoading}
              startIcon={
                blockingLoading ? (
                  <CircularProgress size={18} color='inherit' />
                ) : null
              }
              sx={{ borderRadius: 2, textTransform: "none" }}
            >
              Apply & next
            </Button>
          </>
        )
      }
    >
      {blockingLoading ? (
        <Box
          sx={{
            height: {
              xs: "min(640px, calc(100vh - 220px))",
              md: "min(560px, calc(100vh - 240px))",
            },
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <DialogLoading
            title={blockingLoadingTitle}
            content={blockingLoadingContent}
          />
        </Box>
      ) : isBatchTagMode ? (
        <BatchScheduleTagMode
          assignments={batchAssignments}
          visibleAssignments={visibleBatchAssignments}
          selectedAssignments={selectedBatchAssignments}
          adjustmentFilter={batchAdjustmentFilter}
          adjustmentFilterOptions={batchAdjustmentFilterOptions}
          schedulesByType={batchSchedulesByType}
          companyType={companyType}
          rowLabel={rowLabel}
          localError={localError}
          applying={applying}
          onFilterChange={setBatchAdjustmentFilter}
          onAssignmentChange={updateBatchAssignment}
          onToggleAssignment={toggleBatchAssignment}
          onSelectAllVisible={selectAllVisibleBatchAssignments}
          onClearVisible={clearVisibleBatchAssignments}
        />
      ) : (
        <Box
          sx={{
            position: "relative",
            minHeight: 0,
          }}
        >
          <Fade
            in={autoSelectionNoticeOpen && Boolean(autoSelectionNotice)}
            timeout={{ enter: 220, exit: 1200 }}
            unmountOnExit
            style={{
              transitionDelay: autoSelectionNoticeOpen ? "100ms" : "0ms",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                bottom: 24,
                left: "50%",
                transform: "translateX(-50%)",
                top: "auto",
                zIndex: 20,
                pointerEvents: "none",

                display: "flex",
                alignItems: "center",
                gap: 1,

                px: 1.75,
                py: 0.9,

                width: "max-content",
                maxWidth: "calc(100% - 32px)",
                borderRadius: 2,
                bgcolor: (theme) => alpha(theme.palette.success.light, 0.1),

                color: "text.secondary",

                border: (theme) =>
                  `1px solid ${alpha(theme.palette.success.main, 0.6)}`,

                backdropFilter: "blur(10px)",

                boxShadow: (theme) =>
                  `0 4px 20px ${alpha(theme.palette.common.black, 0.06)}`,

                transition: "all 200ms ease",
              }}
            >
              <CheckCircleOutlineIcon
                sx={{
                  fontSize: 18,
                  opacity: 0.72,
                }}
                color='success'
              />

              <Typography
                variant='body2'
                sx={{
                  fontWeight: 500,
                  letterSpacing: 0.1,
                  lineHeight: 1.2,
                  color: "success.main",
                }}
              >
                {autoSelectionNotice}
              </Typography>
            </Box>
          </Fade>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "minmax(0, 1fr)",
                md:
                  selectedRows.length > 0
                    ? "minmax(0, 1fr) 320px"
                    : "minmax(0, 1fr)",
              },
              gridTemplateRows: {
                md: localError ? "auto minmax(0, 1fr)" : "minmax(0, 1fr)",
              },
              gap: 2,
              height: {
                xs: "auto",
                md: "max(420px, min(640px, calc(100vh - 240px)))",
              },
              minHeight: 0,
              overflow: "hidden",
            }}
          >
            {localError && (
              <Alert severity='error' sx={{ gridColumn: "1 / -1" }}>
                {localError}
              </Alert>
            )}

            <Paper
              elevation={0}
              sx={{
                ...panelSx,
                display: "flex",
                flexDirection: "column",
                flex: 1,
                minWidth: 0,
                minHeight: { xs: 240, md: 200 },
              }}
            >
              <Stack
                direction='row'
                alignItems='center'
                justifyContent='space-between'
                sx={{
                  px: 2,
                  pt: 2,
                  gap: 1.5,
                  flexWrap: { xs: "wrap", sm: "nowrap" },
                }}
              >
                <Typography
                  variant='subtitle1'
                  fontWeight={700}
                  sx={{ flexShrink: 0 }}
                >
                  Select rows
                </Typography>
                <Stack
                  direction='row'
                  alignItems='center'
                  role='group'
                  aria-label='Filter rows by adjustment'
                  sx={(theme) => ({
                    flex: { sm: 1 },
                    minWidth: 0,
                    width: { xs: "100%", sm: "auto" },
                    maxWidth: { sm: 350 },
                    border: "1px solid",
                    borderColor: alpha(theme.palette.primary.main, 0.25),
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.primary.main, 0.05),
                  })}
                >
                  <Stack
                    direction='row'
                    spacing={0.5}
                    alignItems='center'
                    sx={{ px: 1, color: "primary.dark", flexShrink: 0 }}
                  >
                    <FilterAltOutlinedIcon sx={{ fontSize: 18 }} />
                    <Typography variant='caption' fontWeight={700}>
                      Filter
                    </Typography>
                  </Stack>
                  <GroupedSelect
                    value={adjustmentFilter}
                    onChange={(event) => {
                      const nextFilter = event.target.value;
                      const nextRows = getRowsByAdjustmentFilter(
                        modeRows,
                        nextFilter
                      );

                      setAdjustmentFilter(nextFilter);
                      setSelectedIds(nextRows.map((item) => item.id));
                    }}
                    options={adjustmentFilterOptions}
                    selectedItemStyle={{ fontWeight: 600 }}
                    placeholder='All adjustments'
                    enableSearch
                    searchPlaceholder='Search adjustments...'
                    size='small'
                    disabled={
                      applying || loadingSchedules || modeRows.length === 0
                    }
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      borderRadius: "0 8px 8px 0 !important",
                      "& .MuiOutlinedInput-notchedOutline": {
                        border: 0,
                        borderLeft: "1px solid",
                        borderColor: "divider",
                      },
                      "& .MuiSelect-select": {
                        fontSize: 13,
                        fontWeight: 600,
                        py: 1,
                        pl: 1,
                      },
                    }}
                  />
                </Stack>
              </Stack>
              <Stack
                direction='column'
                alignItems='stretch'
                sx={{
                  px: 2,
                  py: 1.5,
                  borderBottom: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Stack
                  direction='row'
                  alignItems='center'
                  sx={{
                    flexWrap: "wrap",
                    gap: 0.5,
                    p: 0.5,
                    borderRadius: 2.5,
                    bgcolor: "action.hover",
                  }}
                >
                  <Stack
                    direction='row'
                    alignItems='center'
                    sx={{
                      borderRadius: 2,
                      overflow: "hidden",
                    }}
                  >
                    <Button
                      size='small'
                      variant='text'
                      onClick={() => changeManagementMode("needsScheduleTag")}
                      disabled={applying || loadingSchedules}
                      sx={modeButtonSx(managementMode === "needsScheduleTag")}
                      aria-pressed={managementMode === "needsScheduleTag"}
                    >
                      Needs tag ({rowsNeedingScheduleTag.length})
                    </Button>
                    <Collapse
                      in={rowsNeedingScheduleTag.length > 0}
                      orientation='horizontal'
                      timeout={520}
                      collapsedSize={0}
                      unmountOnExit
                    >
                      <Stack
                        direction='row'
                        alignItems='stretch'
                        sx={{
                          animation:
                            "batchTagReveal 560ms cubic-bezier(0.16, 1, 0.3, 1)",
                          "@keyframes batchTagReveal": {
                            "0%": {
                              opacity: 0,
                              transform: "translateX(-36px)",
                            },
                            "65%": {
                              opacity: 1,
                              transform: "translateX(4px)",
                            },
                            "100%": {
                              opacity: 1,
                              transform: "translateX(0)",
                            },
                          },
                        }}
                      >
                        <Divider orientation='vertical' flexItem />
                        <Tooltip
                          title='Batch tag rows that need schedules'
                          arrow
                        >
                          <span>
                            <Button
                              size='small'
                              variant='text'
                              onClick={openBatchTagMode}
                              disabled={blockingLoading}
                              startIcon={<PlaylistAddCheckIcon />}
                              sx={{
                                color: "primary.main",
                                fontWeight: 600,
                                textTransform: "none",
                                minWidth: "auto",
                                borderRadius: 0,
                                whiteSpace: "nowrap",
                              }}
                            >
                              Batch
                            </Button>
                          </span>
                        </Tooltip>
                      </Stack>
                    </Collapse>
                  </Stack>
                  <Button
                    size='small'
                    variant='text'
                    onClick={() => changeManagementMode("tagged")}
                    disabled={applying || loadingSchedules}
                    sx={modeButtonSx(managementMode === "tagged")}
                    aria-pressed={managementMode === "tagged"}
                  >
                    Tagged ({taggedRows.length})
                  </Button>
                  <Button
                    size='small'
                    variant='text'
                    onClick={() => changeManagementMode("noAdjustment")}
                    disabled={applying || loadingSchedules}
                    sx={modeButtonSx(managementMode === "noAdjustment")}
                    aria-pressed={managementMode === "noAdjustment"}
                  >
                    No adjustment ({rowsWithoutAdjustment.length})
                  </Button>
                  <Divider orientation='vertical' flexItem sx={{ mx: 0.5 }} />
                  <Tooltip title='Select all visible rows' arrow>
                    <span>
                      <IconButton
                        size='small'
                        onClick={selectAll}
                        disabled={applying || loadingSchedules}
                        aria-label='Select all available rows'
                      >
                        <SelectAllIcon fontSize='small' />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip
                    title='Select rows with the same adjustment type'
                    arrow
                  >
                    <span>
                      <IconButton
                        size='small'
                        onClick={selectSameAdjustmentRows}
                        disabled={
                          applying ||
                          loadingSchedules ||
                          visibleRows.length === 0
                        }
                        aria-label='Select rows with the same adjustment type'
                      >
                        <CategoryOutlinedIcon fontSize='small' />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title='Clear selected rows' arrow>
                    <span>
                      <IconButton
                        size='small'
                        onClick={clearSelection}
                        disabled={applying || loadingSchedules}
                        aria-label='Clear selected rows'
                      >
                        <ClearAllIcon fontSize='small' />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Chip
                    label={`${selectedRows.length} selected`}
                    size='small'
                    color='primary'
                    variant='outlined'
                    sx={{ ml: "auto", fontWeight: 600, height: 24 }}
                  />
                </Stack>
              </Stack>

              {visibleRows.length === 0 ? (
                <Box
                  sx={{
                    m: 3,
                    p: 5,
                    border: "1px dashed",
                    borderColor: "divider",
                    borderRadius: 2,
                    textAlign: "center",
                  }}
                >
                  <Typography variant='body2' color='text.secondary'>
                    {managementMode === "needsScheduleTag"
                      ? "No rows currently need schedule tagging."
                      : managementMode === "noAdjustment"
                        ? "No rows without adjustments are available."
                        : "No tagged rows are available."}
                  </Typography>
                </Box>
              ) : (
                <Stack
                  spacing={0.75}
                  sx={{
                    p: 1.5,
                    overflow: "auto",
                    flex: 1,
                    maxHeight: { xs: 360, md: "none" },
                  }}
                >
                  {visibleRows.map((item) => {
                    const selected = selectedIds.includes(item.id);
                    const needsTag = rowNeedsScheduleTag(item.row);
                    const metaText = [
                      item.section,
                      item.row.taxAdjustment,
                      item.row.schedule?.scheduleTitle,
                    ]
                      .filter(Boolean)
                      .join(" · ");

                    return (
                      <Paper
                        key={item.id}
                        elevation={0}
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "auto minmax(0, 1fr)",
                          gap: 1.25,
                          alignItems: "center",
                          minHeight: 58,
                          flexShrink: 0,
                          px: 1.25,
                          py: 1.25,
                          border: "1px solid",
                          borderColor: selected ? "primary.main" : "divider",
                          bgcolor: (theme) =>
                            selected
                              ? alpha(theme.palette.primary.main, 0.05)
                              : "background.paper",
                          borderRadius: 2,
                        }}
                      >
                        <Checkbox
                          checked={selected}
                          onChange={() => toggleRow(item.id)}
                          disabled={applying || loadingSchedules}
                          inputProps={{
                            "aria-label": `Select ${item.row.name}`,
                          }}
                          sx={{ p: 0.75 }}
                        />
                        <Box sx={{ minWidth: 0 }}>
                          <Stack
                            direction={{ xs: "column", sm: "row" }}
                            alignItems='baseline'
                            justifyContent='space-between'
                            spacing={{ xs: 0.25, sm: 1.5 }}
                          >
                            <Typography
                              variant='body2'
                              fontWeight={600}
                              color={selected ? "primary.dark" : "text.primary"}
                              sx={{ minWidth: 0, overflowWrap: "anywhere" }}
                            >
                              {item.row.name}
                            </Typography>
                            <Typography
                              variant='body2'
                              fontWeight={700}
                              sx={{
                                whiteSpace: "nowrap",
                                flexShrink: 0,
                                fontVariantNumeric: "tabular-nums",
                              }}
                            >
                              {formatAmount(item.row.amount)}
                            </Typography>
                          </Stack>
                          {metaText && (
                            <Typography
                              variant='caption'
                              color='text.secondary'
                              sx={{
                                display: "block",
                                mt: 0.5,
                                overflowWrap: "anywhere",
                              }}
                            >
                              {metaText}
                            </Typography>
                          )}
                          {needsTag && (
                            <Chip
                              label='Needs tag'
                              size='small'
                              variant='outlined'
                              color='warning'
                              sx={{ mt: 0.75, height: 21, fontSize: 11 }}
                            />
                          )}
                        </Box>
                      </Paper>
                    );
                  })}
                </Stack>
              )}
            </Paper>

            <Collapse
              in={selectedRows.length > 0}
              timeout={220}
              unmountOnExit
              sx={{
                minWidth: 0,
                maxHeight: { md: "100%" },
                "&.MuiCollapse-entered": { overflowY: { md: "auto" } },
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  ...panelSx,
                  p: 2,
                  flexShrink: 0,
                  borderColor: (theme) =>
                    alpha(theme.palette.primary.main, 0.25),
                  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.03),
                }}
              >
                <Fade in={selectedRows.length > 0} timeout={260}>
                  <Stack spacing={1.25}>
                    <Stack
                      direction={{ xs: "column", md: "row" }}
                      alignItems={{ xs: "stretch", md: "center" }}
                      justifyContent='space-between'
                      spacing={1}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          variant='subtitle1'
                          fontWeight={700}
                          color='primary.dark'
                        >
                          Apply to selected rows
                        </Typography>
                        <Typography
                          variant='body2'
                          color='text.secondary'
                          sx={{ mt: 0.5, overflowWrap: "anywhere" }}
                        >
                          {taggingActionSummary}
                        </Typography>
                      </Box>
                    </Stack>

                    {!hasAdjustmentChoice && (
                      <Alert
                        severity={"info"}
                        sx={{ borderRadius: 2, py: 0.75 }}
                      >
                        Choose an adjustment for these rows.
                      </Alert>
                    )}

                    <Box sx={{ position: "relative" }}>
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "minmax(0, 1fr)",
                          gap: 1.25,
                          alignItems: "start",
                          filter: loadingSchedules ? "blur(1.5px)" : "none",
                          opacity: loadingSchedules ? 0.58 : 1,
                          pointerEvents: loadingSchedules ? "none" : "auto",
                          transition: "filter 160ms ease, opacity 160ms ease",
                        }}
                      >
                        <Box>
                          <Typography
                            variant='caption'
                            color='text.secondary'
                            sx={{
                              display: "block",
                              mb: 0.75,
                              fontWeight: 700,
                            }}
                          >
                            Adjustment
                          </Typography>
                          <GroupedSelect
                            value={adjustmentType}
                            onChange={handleAdjustmentTypeChange}
                            options={setupAdjustmentOptions}
                            placeholder='Choose an adjustment type'
                            enableSearch
                            searchPlaceholder='Search adjustments...'
                            size='small'
                            disabled={applying}
                            fullWidth
                          />
                        </Box>

                        {!hasAdjustmentChoice && <Box />}

                        {hasAdjustmentChoice && (
                          <Stack
                            direction='column'
                            spacing={1}
                            alignItems='stretch'
                            sx={{
                              minWidth: 0,
                              flexWrap: "nowrap",
                              gap: 1,
                              "& > .MuiStack-root": { width: "100%" },
                              "& > :not(style) ~ :not(style)": { ml: 0, mt: 0 },
                            }}
                          >
                            {needsRowType && (
                              <Stack spacing={0.75} sx={{ minWidth: 0 }}>
                                <Typography
                                  variant='caption'
                                  color='text.secondary'
                                  sx={{ fontWeight: 700 }}
                                >
                                  Tag these rows as
                                </Typography>
                                <TextField
                                  value={rowType}
                                  onChange={(event) =>
                                    setRowType(event.target.value)
                                  }
                                  select
                                  required
                                  error={!rowType}
                                  fullWidth
                                  size='small'
                                  disabled={
                                    applying || rowTypeOptions.length === 1
                                  }
                                  color={!rowType ? "warning" : "primary"}
                                >
                                  <MenuItem value=''>
                                    <em>Choose a category</em>
                                  </MenuItem>
                                  {rowTypeOptions.map((option) => (
                                    <MenuItem
                                      key={option.value}
                                      value={option.value}
                                    >
                                      {option.label}
                                    </MenuItem>
                                  ))}
                                </TextField>
                              </Stack>
                            )}

                            {needsSchedule && (
                              <>
                                <Stack spacing={0.75} sx={{ minWidth: 0 }}>
                                  <Typography
                                    variant='caption'
                                    color='text.secondary'
                                    sx={{ fontWeight: 700 }}
                                  >
                                    Schedule option
                                  </Typography>
                                  <TextField
                                    value={scheduleMode}
                                    onChange={(event) =>
                                      setScheduleMode(event.target.value)
                                    }
                                    select
                                    fullWidth
                                    size='small'
                                    disabled={applying}
                                    sx={{ backgroundColor: "background.paper" }}
                                  >
                                    <MenuItem value='existing'>
                                      <Stack
                                        direction='row'
                                        alignItems='center'
                                        spacing={1.5}
                                        width='100%'
                                      >
                                        <HistoryOutlined
                                          fontSize='small'
                                          color='action'
                                        />
                                        <span style={{ flex: 1 }}>
                                          Use existing schedule
                                        </span>
                                      </Stack>
                                    </MenuItem>

                                    <MenuItem value='new'>
                                      <Stack
                                        direction='row'
                                        alignItems='center'
                                        spacing={1.5}
                                        width='100%'
                                      >
                                        <AddCircleOutline
                                          fontSize='small'
                                          color='primary'
                                        />
                                        <span style={{ flex: 1 }}>
                                          Create a new schedule
                                        </span>
                                      </Stack>
                                    </MenuItem>
                                  </TextField>
                                </Stack>

                                {scheduleMode === "existing" ? (
                                  <Stack spacing={0.75} sx={{ minWidth: 0 }}>
                                    <Typography
                                      variant='caption'
                                      color='text.secondary'
                                      sx={{ fontWeight: 700 }}
                                    >
                                      Schedule
                                    </Typography>
                                    <TextField
                                      value={scheduleId}
                                      onChange={(event) =>
                                        setScheduleId(event.target.value)
                                      }
                                      select
                                      fullWidth
                                      size='small'
                                      disabled={applying || loadingSchedules}
                                      sx={{
                                        backgroundColor: "background.paper",
                                      }}
                                    >
                                      {schedules.length === 0 ? (
                                        <MenuItem value='' disabled>
                                          <em>No schedule available</em>
                                        </MenuItem>
                                      ) : (
                                        schedules.map((schedule) => (
                                          <MenuItem
                                            key={schedule.sch_id}
                                            value={String(schedule.sch_id)}
                                            sx={{
                                              display: "flex",
                                              justifyContent: "space-between",
                                              alignItems: "center",
                                            }}
                                          >
                                            <span
                                              style={{
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                whiteSpace: "nowrap",
                                                marginRight: "8px",
                                              }}
                                            >
                                              {schedule.scheduleTitle}
                                            </span>

                                            <Chip
                                              label={`Sch No ${schedule.scheduleNo}`}
                                              size='small'
                                              color='primary'
                                              variant='outlined'
                                              sx={{
                                                fontWeight: "bold",
                                                fontSize: "0.75rem",
                                                height: "20px",
                                                flexShrink: 0,
                                              }}
                                            />
                                          </MenuItem>
                                        ))
                                      )}
                                    </TextField>
                                  </Stack>
                                ) : (
                                  <Stack spacing={0.75} sx={{ minWidth: 0 }}>
                                    <Typography
                                      variant='caption'
                                      color='text.secondary'
                                      sx={{ fontWeight: 700 }}
                                    >
                                      New schedule title
                                    </Typography>
                                    <TextField
                                      value={scheduleTitle}
                                      onChange={(event) =>
                                        setScheduleTitle(event.target.value)
                                      }
                                      fullWidth
                                      size='small'
                                      placeholder='Enter schedule title'
                                      disabled={applying}
                                      sx={{
                                        backgroundColor: "background.paper",
                                      }}
                                    />
                                  </Stack>
                                )}
                              </>
                            )}
                          </Stack>
                        )}
                      </Box>

                      {loadingSchedules && (
                        <Box
                          sx={{
                            position: "absolute",
                            inset: 0,
                            zIndex: 1,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            borderRadius: 2,
                            bgcolor: (theme) =>
                              alpha(theme.palette.background.paper, 0.52),
                            backdropFilter: "blur(2px)",
                          }}
                        >
                          <Stack
                            direction='row'
                            spacing={1}
                            alignItems='center'
                            sx={{
                              px: 1.5,
                              py: 0.9,
                              borderRadius: 999,
                              bgcolor: "background.paper",
                              border: "1px solid",
                              borderColor: "divider",
                              boxShadow: (theme) =>
                                `0 4px 12px ${alpha(theme.palette.common.black, 0.06)}`,
                            }}
                          >
                            <CircularProgress
                              size={16}
                              thickness={5}
                              color='primary'
                            />
                            <Typography variant='body2' color='text.secondary'>
                              Loading...
                            </Typography>
                          </Stack>
                        </Box>
                      )}
                    </Box>
                  </Stack>
                </Fade>
              </Paper>
            </Collapse>
          </Box>
        </Box>
      )}
    </BaseDialog>
  );
}

function getTaggingActionSummary({
  selectedCount,
  adjustmentType,
  hasAdjustmentChoice,
  needsSchedule,
  needsRowType,
  rowTypeLabel,
  scheduleMode,
  scheduleTitle,
  selectedSchedule,
}) {
  const lineLabel = `${selectedCount} selected line${
    selectedCount === 1 ? "" : "s"
  }`;

  if (!hasAdjustmentChoice) {
    return `Tag ${lineLabel}`;
  }

  if (!needsSchedule) {
    return `Set ${lineLabel} to ${adjustmentType || "No Adjustment"}`;
  }

  const schedulePhrase =
    scheduleMode === "existing"
      ? selectedSchedule?.scheduleTitle || "an existing schedule"
      : scheduleTitle || "a new schedule";
  const methodPhrase = scheduleMode === "existing" ? "using" : "by creating";
  const rowTypePhrase =
    needsRowType && rowTypeLabel ? ` as ${rowTypeLabel}` : "";

  return `Tag ${lineLabel} to ${adjustmentType}${rowTypePhrase} ${methodPhrase} ${schedulePhrase}`;
}

function BatchScheduleTagMode({
  assignments,
  visibleAssignments,
  selectedAssignments,
  adjustmentFilter,
  adjustmentFilterOptions,
  schedulesByType,
  companyType,
  rowLabel,
  localError,
  applying,
  onFilterChange,
  onAssignmentChange,
  onToggleAssignment,
  onSelectAllVisible,
  onClearVisible,
}) {
  const newScheduleOwnersByType = getBatchNewScheduleOwners(assignments);

  return (
    <Box
      sx={{
        height: {
          xs: "auto",
          md: "min(560px, calc(100vh - 240px))",
        },
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        gap: 1.75,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          ...panelSx,
          p: 2,
          flexShrink: 0,
          bgcolor: "action.hover",
          display: "flex",
          alignItems: { xs: "stretch", md: "center" },
          justifyContent: "space-between",
          gap: 1.5,
          flexDirection: { xs: "column", md: "row" },
        }}
      >
        <Stack direction='row' spacing={1.25} alignItems='center'>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 2,
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
              color: "primary.main",
              flexShrink: 0,
              display: "grid",
              placeItems: "center",
            }}
          >
            <PlaylistAddCheckIcon fontSize='small' />
          </Box>
          <Box>
            <Typography variant='subtitle1' fontWeight={700}>
              Tag rows to schedules
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              {assignments.length} untagged {rowLabel} available,{" "}
              {selectedAssignments.length} selected.
            </Typography>
          </Box>
        </Stack>

        <Stack
          direction='row'
          alignItems='center'
          sx={{ flexWrap: "wrap", gap: 1 }}
        >
          <GroupedSelect
            value={adjustmentFilter}
            onChange={(event) => onFilterChange(event.target.value)}
            options={adjustmentFilterOptions}
            placeholder='All schedule types'
            enableSearch
            searchPlaceholder='Search schedule types...'
            size='small'
            disabled={applying || assignments.length === 0}
            sx={{ minWidth: 0, width: { xs: "100%", sm: 220 } }}
          />
          <Tooltip title='Select all visible rows' arrow>
            <span>
              <IconButton
                size='small'
                onClick={onSelectAllVisible}
                disabled={applying || visibleAssignments.length === 0}
                aria-label='Select all visible batch rows'
              >
                <SelectAllIcon fontSize='small' />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title='Clear visible rows' arrow>
            <span>
              <IconButton
                size='small'
                onClick={onClearVisible}
                disabled={applying || visibleAssignments.length === 0}
                aria-label='Clear visible batch rows'
              >
                <ClearAllIcon fontSize='small' />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Paper>

      {localError && <Alert severity='error'>{localError}</Alert>}

      {assignments.length === 0 ? (
        <Box
          sx={{
            flex: 1,
            display: "grid",
            placeItems: "center",
            border: "1px dashed",
            borderColor: "divider",
            borderRadius: 2,
            textAlign: "center",
            p: 4,
          }}
        >
          <Typography variant='body2' color='text.secondary'>
            No rows currently need schedule tagging.
          </Typography>
        </Box>
      ) : visibleAssignments.length === 0 ? (
        <Box
          sx={{
            flex: 1,
            display: "grid",
            placeItems: "center",
            border: "1px dashed",
            borderColor: "divider",
            borderRadius: 2,
            textAlign: "center",
            p: 4,
          }}
        >
          <Typography variant='body2' color='text.secondary'>
            No rows match this schedule type filter.
          </Typography>
        </Box>
      ) : (
        <Stack
          spacing={1.5}
          sx={{
            minHeight: 0,
            overflow: { xs: "visible", md: "auto" },
            pr: 0.5,
          }}
        >
          {visibleAssignments.map((assignment) => {
            const schedulesForType =
              schedulesByType[assignment.scheduleType] || [];
            const rowTypeOptions = getBatchRowTypeOptions(
              assignment,
              companyType
            );
            const needsRowType = rowTypeOptions.length > 0;
            const isReady = isBatchAssignmentReady(
              assignment,
              schedulesByType,
              companyType
            );
            const newScheduleOwner = newScheduleOwnersByType.get(
              assignment.scheduleType
            );
            const isNewScheduleOwner =
              newScheduleOwner?.id === assignment.id &&
              assignment.scheduleId === BATCH_NEW_SCHEDULE_ID;
            const newScheduleTitle = getBatchNewScheduleTitle(
              newScheduleOwner || assignment
            );
            const newScheduleTitleLabel =
              newScheduleTitle || "Untitled new schedule";
            const needsNewScheduleTitle =
              isNewScheduleOwner && !newScheduleTitle;
            const metaText = [assignment.section, assignment.adjustmentType]
              .filter(Boolean)
              .join(" / ");

            return (
              <Paper
                key={assignment.id}
                elevation={0}
                sx={{
                  p: 2,
                  flexShrink: 0,
                  minHeight: 130,
                  border: "1px solid",
                  borderColor: assignment.selected ? "primary.main" : "divider",
                  bgcolor: (theme) =>
                    assignment.selected
                      ? alpha(theme.palette.primary.main, 0.03)
                      : "background.paper",
                  borderRadius: 2,
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "auto minmax(0, 1fr)",
                    md: "auto minmax(0, 1fr)",
                  },
                  columnGap: 1.25,
                  rowGap: 0.9,
                  alignItems: "flex-start",
                }}
              >
                <Checkbox
                  checked={assignment.selected}
                  onChange={() => onToggleAssignment(assignment.id)}
                  disabled={applying}
                  inputProps={{ "aria-label": `Select ${assignment.row.name}` }}
                  sx={{ mt: -0.5 }}
                />

                <Box sx={{ minWidth: 0 }}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: { xs: "column", sm: "row" },
                      alignItems: "baseline",
                      justifyContent: "space-between",
                      gap: 1.5,
                      minWidth: 0,
                    }}
                  >
                    <Typography
                      variant='body2'
                      fontWeight={700}
                      sx={{ minWidth: 0, overflowWrap: "anywhere" }}
                    >
                      {assignment.row.name}
                    </Typography>
                    <Typography
                      variant='body2'
                      fontWeight={700}
                      sx={{
                        whiteSpace: "nowrap",
                        flexShrink: 0,
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {formatAmount(assignment.row.amount)}
                    </Typography>
                  </Box>
                  <Typography
                    variant='caption'
                    color='text.secondary'
                    sx={{ display: "block", mt: 0.5, overflowWrap: "anywhere" }}
                  >
                    {metaText}
                  </Typography>
                  {assignment.selected && (
                    <Chip
                      label={isReady ? "Ready to tag" : "Complete selection"}
                      size='small'
                      variant='outlined'
                      color={isReady ? "success" : "warning"}
                      sx={{ mt: 1, height: 23, fontSize: 11, fontWeight: 600 }}
                    />
                  )}
                </Box>

                <Stack
                  direction={{ xs: "column", md: "row" }}
                  spacing={1}
                  sx={{
                    gridColumn: "2 / -1",
                    minWidth: 0,
                    pt: 1.5,
                    mt: 0.5,
                    borderTop: "1px solid",
                    borderColor: "divider",
                    alignItems: { xs: "stretch", md: "center" },
                    "& .MuiTextField-root": {
                      bgcolor: "background.paper",
                    },
                    "& .MuiInputBase-root": {
                      minHeight: 36,
                    },
                    "& .MuiInputBase-input": {
                      py: 0.75,
                    },
                  }}
                >
                  <TextField
                    label='Schedule'
                    value={assignment.scheduleId}
                    onChange={(event) =>
                      onAssignmentChange(assignment.id, {
                        scheduleId: event.target.value,
                      })
                    }
                    select
                    required
                    error={assignment.selected && !assignment.scheduleId}
                    size='small'
                    disabled={applying || !assignment.selected}
                    sx={{ flex: 1, minWidth: { xs: 0, md: 260 } }}
                  >
                    <MenuItem value={BATCH_NEW_SCHEDULE_ID}>
                      {isNewScheduleOwner
                        ? `Create new schedule: ${newScheduleTitleLabel}`
                        : newScheduleOwner
                          ? `Use new schedule: ${newScheduleTitleLabel}`
                          : `Create new schedule: ${newScheduleTitleLabel}`}
                    </MenuItem>
                    {schedulesForType.length === 0 ? (
                      <MenuItem value='' disabled>
                        <em>No schedule available</em>
                      </MenuItem>
                    ) : (
                      schedulesForType.map((schedule) => (
                        <MenuItem
                          key={schedule.sch_id}
                          value={String(schedule.sch_id)}
                        >
                          {schedule.scheduleTitle} - No. {schedule.scheduleNo}
                        </MenuItem>
                      ))
                    )}
                  </TextField>

                  {isNewScheduleOwner && (
                    <TextField
                      label='New schedule title'
                      value={assignment.scheduleTitle}
                      onChange={(event) =>
                        onAssignmentChange(assignment.id, {
                          scheduleTitle: event.target.value,
                        })
                      }
                      required
                      error={needsNewScheduleTitle}
                      size='small'
                      disabled={applying || !assignment.selected}
                      placeholder='Enter schedule title'
                      sx={{ flex: 1, minWidth: { xs: 0, md: 240 } }}
                    />
                  )}

                  {needsRowType && (
                    <TextField
                      label='Tag this row as'
                      value={assignment.rowType}
                      onChange={(event) =>
                        onAssignmentChange(assignment.id, {
                          rowType: event.target.value,
                        })
                      }
                      select
                      required
                      error={assignment.selected && !assignment.rowType}
                      size='small'
                      disabled={
                        applying ||
                        !assignment.selected ||
                        rowTypeOptions.length === 1
                      }
                      sx={{ width: { xs: "100%", md: 180 } }}
                    >
                      <MenuItem value=''>
                        <em>Choose a category</em>
                      </MenuItem>
                      {rowTypeOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                </Stack>
              </Paper>
            );
          })}
        </Stack>
      )}
    </Box>
  );
}

function flattenRows(rowsBySection, sectionKeys = SECTION_KEYS) {
  return sectionKeys
    .flatMap((section) =>
      (rowsBySection[section] || []).map((row, index) => ({
        id: `${section}:${row.row_id || index}`,
        section,
        index,
        row,
      }))
    )
    .filter(
      (item) => item.row?.name || item.row?.amount || item.row?.taxAdjustment
    );
}

function mergeBatchAssignments(rowsNeedingScheduleTag, previous, companyType) {
  const previousById = new Map(
    previous.map((assignment) => [assignment.id, assignment])
  );

  return rowsNeedingScheduleTag.map((item) => {
    const next = createBatchAssignment(item, companyType);
    const prev = previousById.get(item.id);

    if (!prev) return next;

    const sameScheduleType = prev.scheduleType === next.scheduleType;

    return {
      ...next,
      selected: prev.selected,
      scheduleId: sameScheduleType ? prev.scheduleId : "",
      scheduleTitle: sameScheduleType
        ? prev.scheduleTitle || next.scheduleTitle
        : next.scheduleTitle,
      rowType: sameScheduleType ? prev.rowType || next.rowType : next.rowType,
    };
  });
}

function createBatchAssignment(item, companyType) {
  const initial = getInitialScheduleState(item.row, false);
  const scheduleType = initial.scheduleType || item.row.taxAdjustment;
  const rowTypeOptions = getBatchRowTypeOptions({ scheduleType }, companyType);
  const defaultRowType =
    initial.targetSchedule.rowType ||
    (rowTypeOptions.length === 1 ? rowTypeOptions[0].value : "");

  return {
    id: item.id,
    section: item.section,
    index: item.index,
    row: item.row,
    adjustmentType: item.row.taxAdjustment,
    scheduleType,
    scheduleTitle: initial.targetSchedule.scheduleTitle || item.row.name,
    rowType: defaultRowType,
    scheduleId: "",
    selected: true,
  };
}

function getBatchRowTypeOptions(assignment, companyType) {
  const resolvedScheduleType = resolveScheduleType(
    assignment.scheduleType,
    companyType
  );

  return ROW_TYPE_OPTIONS[resolvedScheduleType] || [];
}

function isBatchAssignmentReady(assignment, schedulesByType, companyType) {
  if (!assignment.scheduleId) return false;

  if (assignment.scheduleId !== BATCH_NEW_SCHEDULE_ID) {
    const schedulesForType = schedulesByType[assignment.scheduleType] || [];
    const scheduleExists = schedulesForType.some(
      (schedule) => String(schedule.sch_id) === String(assignment.scheduleId)
    );

    if (!scheduleExists) return false;
  } else if (!getBatchNewScheduleTitle(assignment)) {
    return false;
  }

  const rowTypeOptions = getBatchRowTypeOptions(assignment, companyType);

  return rowTypeOptions.length === 0 || Boolean(assignment.rowType);
}

function getBatchNewScheduleTitle(assignment) {
  return (assignment?.scheduleTitle || "").trim();
}

function getBatchNewScheduleOwners(assignments) {
  return assignments.reduce((ownersByType, assignment) => {
    if (
      !assignment.selected ||
      assignment.scheduleId !== BATCH_NEW_SCHEDULE_ID ||
      ownersByType.has(assignment.scheduleType)
    ) {
      return ownersByType;
    }

    ownersByType.set(assignment.scheduleType, assignment);
    return ownersByType;
  }, new Map());
}

function normalizeBatchNewScheduleOwners(nextAssignments, previousAssignments) {
  const previousOwners = getBatchNewScheduleOwners(previousAssignments);
  if (previousOwners.size === 0) return nextAssignments;

  const nextOwners = getBatchNewScheduleOwners(nextAssignments);
  const inheritedTitlesById = new Map();

  previousOwners.forEach((previousOwner, scheduleType) => {
    const nextOwner = nextOwners.get(scheduleType);

    if (!nextOwner || nextOwner.id === previousOwner.id) return;

    const previousTitle = getBatchNewScheduleTitle(previousOwner);
    if (previousTitle) {
      inheritedTitlesById.set(nextOwner.id, previousTitle);
    }
  });

  if (inheritedTitlesById.size === 0) return nextAssignments;

  return nextAssignments.map((assignment) =>
    inheritedTitlesById.has(assignment.id)
      ? { ...assignment, scheduleTitle: inheritedTitlesById.get(assignment.id) }
      : assignment
  );
}

function rowNeedsScheduleTag(row) {
  return (
    Boolean(row?.taxAdjustment) &&
    !BASIC_ADJUSTMENTS.includes(row.taxAdjustment) &&
    !row.schedule
  );
}

function rowHasNoAdjustment(row) {
  return BASIC_ADJUSTMENTS.includes(row?.taxAdjustment || "") && !row?.schedule;
}

function getRowsByAdjustmentFilter(rows, adjustmentFilter) {
  if (adjustmentFilter === ALL_ADJUSTMENT_FILTER) return rows;

  const targetAdjustment =
    adjustmentFilter === NO_ADJUSTMENT_FILTER ? "" : adjustmentFilter;

  return rows.filter(
    (item) => (item.row.taxAdjustment || "") === targetAdjustment
  );
}

function cloneRowsBySection(rowsBySection, sectionKeys = SECTION_KEYS) {
  return sectionKeys.reduce((acc, section) => {
    acc[section] = (rowsBySection[section] || []).map((row) => ({ ...row }));
    return acc;
  }, {});
}

async function applyExistingSchedule({
  item,
  row,
  scheduleId,
  schedules,
  rowType,
  companyType,
  tcId,
  detailsId,
  adjustmentType,
  reference,
  tagSchedule,
}) {
  const schedule = schedules.find(
    (candidate) => String(candidate.sch_id) === String(scheduleId)
  );
  if (!schedule) {
    throw new Error("Please select an available schedule.");
  }

  return tagRowToSchedule({
    item,
    row,
    schedule,
    rowType,
    companyType,
    tcId,
    detailsId,
    adjustmentType,
    reference,
    tagSchedule,
  });
}

async function tagRowToSchedule({
  item,
  row,
  schedule,
  rowType,
  companyType,
  tcId,
  detailsId,
  adjustmentType,
  reference,
  tagSchedule,
}) {
  const { rowMap, dataMap, refObj, scheduleType } = buildSchedulePayload({
    tcId,
    detailsId,
    scheduleId: schedule.sch_id,
    row,
    rowIndex: item.index,
    section: item.section,
    selectedScheduleType: adjustmentType,
    targetSchedule: {
      scheduleTitle: schedule.scheduleTitle,
      rowType,
    },
    reference,
    companyType,
  });

  return tagSchedule(
    {
      ...dataMap,
      rows: rowMap[scheduleType],
    },
    {
      ...refObj,
      schedule,
    }
  );
}

async function applyNewSchedule({
  item,
  row,
  scheduleTitle,
  rowType,
  companyType,
  tcId,
  detailsId,
  adjustmentType,
  reference,
  createTagSchedule,
}) {
  const { rowMap, dataMap, refObj, scheduleType } = buildSchedulePayload({
    tcId,
    detailsId,
    scheduleId: null,
    row,
    rowIndex: item.index,
    section: item.section,
    selectedScheduleType: adjustmentType,
    targetSchedule: {
      scheduleTitle,
      rowType,
    },
    reference,
    companyType,
  });

  return createTagSchedule(
    {
      scheduleTitle,
      ...dataMap,
      scheduleType,
      rows: rowMap[scheduleType],
    },
    refObj
  );
}
